import { Layout } from "@/components/Layout";
import { SeoHead } from "@/components/SeoHead";
import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PHONE_HREF, PHONE_NUMBER } from "@/components/EmergencyBanner";

const articles = [
  {
    slug: "why-well-pump-stopped-working",
    title: "Why Did My Well Pump Stop Working?",
    excerpt: "Your well pump stopped and you have no water. Here are the most common causes and what you should check before calling a technician.",
    content: [
      { heading: "Check Your Circuit Breaker First", text: "Before you panic, check your electrical panel. Well pumps run on a dedicated breaker that can trip during power surges or storms. If the breaker is tripped, reset it once. If it trips again immediately, do not reset it again — that indicates an electrical problem that needs professional diagnosis." },
      { heading: "Pressure Switch Issues", text: "The pressure switch tells your pump when to turn on and off. Over time, the contacts can corrode or the diaphragm can fail. If you hear a clicking sound near your pressure tank but the pump doesn't start, the pressure switch is a likely culprit." },
      { heading: "Pump Motor Failure", text: "Submersible pump motors run in harsh conditions — submerged in water hundreds of feet down a well casing. After 8-15 years, the motor windings can fail, bearings can wear out, or the impeller can corrode. When the motor fails, replacement is typically the best option." },
      { heading: "Don't Pull the Pipe Yourself", text: "It's tempting to try pulling the pump yourself, but a submersible pump can weigh 50-100+ pounds and sits at the end of hundreds of feet of pipe. Without the right equipment and experience, you risk damaging your well casing or losing the pump down the hole. Call a pro." },
    ],
  },
  {
    slug: "signs-well-pump-failing",
    title: "Signs Your Well Pump Is Failing",
    excerpt: "Well pumps don't fail overnight. Here are the warning signs that your pump is on its way out and what to do about it.",
    content: [
      { heading: "Sputtering Faucets", text: "Air in your water lines — sputtering and spitting at the tap — often means your pump is drawing air. This could indicate a failing foot valve, cracked drop pipe, or a pump that's starting to fail." },
      { heading: "Fluctuating Water Pressure", text: "If your water pressure bounces up and down during use, your pressure tank may be waterlogged or your pump may be cycling too rapidly. Both are signs of system stress." },
      { heading: "Higher Electric Bills", text: "A failing pump motor draws more electricity as it struggles. If your electric bill spikes without other explanation, your well pump could be the cause." },
      { heading: "Dirty or Sandy Water", text: "If your water suddenly turns brown or contains sediment, your pump may have dropped below the water level or your well screen may be deteriorating." },
    ],
  },
  {
    slug: "well-pump-running-constantly",
    title: "Why Is My Well Pump Running Constantly?",
    excerpt: "A well pump that won't shut off is running up your electric bill and wearing itself out fast. Here's what causes it.",
    content: [
      { heading: "Pressure Switch Problems", text: "If the pressure switch can't detect when the tank has reached cut-off pressure, the pump keeps running. Corroded contacts or a bad diaphragm are common causes." },
      { heading: "Water Leak in the System", text: "A leak anywhere in your plumbing — even a running toilet — means the pressure never reaches cut-off. The pump runs to keep up with the lost water." },
      { heading: "Waterlogged Pressure Tank", text: "When the bladder in your pressure tank fails, the tank fills with water and can't maintain air pressure. The pump cycles rapidly or runs continuously." },
      { heading: "Failing Check Valve", text: "The check valve prevents water from flowing back down the well. If it fails, water drains back after the pump stops, and the pump has to refill the tank repeatedly." },
    ],
  },
  {
    slug: "no-water-from-well",
    title: "What To Do If Your Well Suddenly Has No Water",
    excerpt: "You turned on the tap and nothing came out. Here's what to do right now and when to call for help.",
    content: [
      { heading: "Don't Panic — Check the Basics", text: "First, check if other faucets work. Check your circuit breaker. Check your pressure gauge on the tank — if it reads zero, the pump isn't running." },
      { heading: "Listen at the Pressure Tank", text: "Go to your pressure tank and listen. If you hear a humming or buzzing, the pump is trying to start but can't. This could be a bad capacitor or seized motor." },
      { heading: "Check for Frozen Pipes", text: "In winter in the Poconos, exposed pipes can freeze. Check any pipes in crawl spaces, basements, or along exterior walls." },
      { heading: "Call a Professional", text: "If the breaker is fine and you don't hear the pump running, it's time to call. Don't keep resetting the breaker — you could cause further damage. A professional can diagnose the issue safely." },
    ],
  },
  {
    slug: "grinder-pump-alarm",
    title: "Grinder Pump Alarm Going Off — What To Do",
    excerpt: "Your grinder pump alarm is sounding. Here's what it means and what steps to take right now.",
    content: [
      { heading: "What the Alarm Means", text: "The alarm indicates that wastewater has reached a critical level in the grinder pump station. This means the pump isn't pumping — either it's failed, it's clogged, or there's an electrical issue." },
      { heading: "Reduce Water Usage Immediately", text: "Stop using water as much as possible. Don't do laundry, don't shower, minimize toilet flushes. You're adding to the problem." },
      { heading: "Check the Breaker", text: "Like well pumps, grinder pumps have a dedicated breaker. Check if it's tripped. If it is, reset it once and wait. If the alarm goes off again, the pump needs professional attention." },
      { heading: "Call for Service", text: "Grinder pump failures are urgent. Sewage backup into your home is a health hazard. Call us immediately — we prioritize grinder pump emergencies." },
    ],
  },
  {
    slug: "how-long-do-well-pumps-last",
    title: "How Long Do Well Pumps Last?",
    excerpt: "Understanding well pump lifespan helps you plan for replacement before an emergency hits.",
    content: [
      { heading: "Typical Lifespan: 8-15 Years", text: "Most submersible well pumps last between 8 and 15 years. Factors that affect lifespan include water quality, usage volume, electrical conditions, and installation quality." },
      { heading: "Water Quality Matters", text: "Pocono Mountain well water can be acidic, high in iron, or contain sediment. All of these accelerate pump wear. A proper filtration system can extend your pump's life significantly." },
      { heading: "Signs It's Time to Replace", text: "If your pump is 10+ years old and you're experiencing frequent repairs, declining performance, or increasing electric bills, replacement is often more cost-effective than continued repairs." },
      { heading: "Proactive Replacement Saves Money", text: "Replacing a pump on your schedule costs less than an emergency replacement. No overtime charges, no rush shipping on parts, and you get to choose the best system for your needs." },
    ],
  },
];

const Resources = () => (
  <Layout>
    <SeoHead
      title="Well Pump Resources & Guides | Semper Fi Pump Service"
      description="Expert guides on well pumps, grinder pumps, and water systems. Troubleshooting tips from 33 years of Pocono Mountains experience."
    />
    <section className="bg-primary">
      <div className="container py-16 md:py-24">
        <h1 className="text-3xl md:text-4xl font-black text-primary-foreground mb-4 border-l-0 pl-0">Resources & Guides</h1>
        <p className="text-primary-foreground/80 max-w-2xl text-pretty">Expert answers to common well pump and water system questions from 33 years of Pocono Mountains experience.</p>
      </div>
    </section>

    <section className="container py-16 md:py-24">
      <div className="grid md:grid-cols-2 gap-6">
        {articles.map((article) => (
          <Link key={article.slug} to={`/resources/${article.slug}`} className="group bg-card rounded-sm p-6 shadow-card hover:shadow-elevated transition-shadow block">
            <h2 className="text-lg font-bold mb-2 border-l-0 pl-0 group-hover:text-accent transition-colors">{article.title}</h2>
            <p className="text-sm text-muted-foreground text-pretty mb-4">{article.excerpt}</p>
            <span className="text-sm font-semibold text-accent inline-flex items-center gap-1">
              Read More <ArrowRight className="w-4 h-4" />
            </span>
          </Link>
        ))}
      </div>
    </section>

    <section className="bg-primary">
      <div className="container py-16 text-center">
        <h2 className="text-2xl font-black text-primary-foreground mb-4 border-l-0 pl-0 inline-block">Have a Question We Didn't Answer?</h2>
        <p className="text-primary-foreground/80 mb-8">Call us. We're happy to help.</p>
        <Button asChild variant="emergency" size="xl">
          <a href={PHONE_HREF}><Phone className="w-5 h-5" />Call {PHONE_NUMBER}</a>
        </Button>
      </div>
    </section>
  </Layout>
);

export default Resources;

export { articles };
